// src/pages/Settings/communication/SMSTemplate.js
import React, { useState, useEffect } from "react";
import { get } from 'helpers/api_helper';
import { Row, Col, Card, CardBody, CardHeader, Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import Datatables from 'pages/utils/table/datatable';
import { useTranslation } from "react-i18next";
import cookieHelper from "helpers/getCookieData";
import {
  RiWhatsappLine,
  RiShieldCheckLine,
  RiEyeLine,
  RiInformationLine,
  RiCheckboxCircleLine,
  RiPriceTag3Line,
} from "react-icons/ri";

const SMSTemplate = () => {
  const { t } = useTranslation();
  const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await get('/notifications/global-templates?template_type=WHATSAPP');
      if (res?.success && res?.body) {
        setRows(res.body);
      }
    } catch (err) {
      console.warn("Notice loading WhatsApp templates:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const getRenderedPreview = (tpl) => {
    if (!tpl) return '';
    const sampleData = {
      patient_name: 'Rahul Sharma',
      doctor_name: 'Dr. Vivek Sharma',
      appointment_date: 'Tomorrow at 10:30 AM',
      clinic_name: clinic?.clinic_name || 'Oralstop Dental Clinic',
      chair_code: 'Chair #1',
      number: clinic?.phone || '+91 9090909090',
      amount: '₹ 1,200',
    };
    let text = tpl.body_text || '';
    Object.keys(sampleData).forEach((key) => {
      text = text.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'gi'), sampleData[key]);
    });
    return text;
  };

  const tableColumns = [
    {
      dataField: 'id',
      text: '#',
      style: { width: '45px', textAlign: 'center' },
      formatter: (cell, row, rowIndex) => <span className="font-weight-semibold text-muted">{rowIndex + 1}</span>,
    },
    {
      dataField: 'title',
      text: t('Template Title & Event'),
      formatter: (cell, row) => (
        <div>
          <strong className="text-dark d-block">{cell}</strong>
          <code className="text-primary font-size-11">{row.template_key}</code>
        </div>
      ),
    },
    {
      dataField: 'meta_template_name',
      text: t('Meta Approved Key'),
      formatter: (cell) => (
        cell ? <code className="text-dark font-size-12">{cell}</code> : <span className="text-muted">—</span>
      ),
    },
    {
      dataField: 'category',
      text: t('Category'),
      formatter: (cell) => (
        <span className="badge bg-light text-dark border font-size-11">{cell || 'UTILITY'}</span>
      ),
    },
    {
      dataField: 'meta_status',
      text: t('Meta Status'),
      style: { textAlign: 'center' },
      formatter: () => (
        <span className="badge bg-soft-success text-success border border-success font-size-11">
          <RiCheckboxCircleLine className="me-1" /> Approved
        </span>
      ),
    },
    {
      dataField: 'actions',
      text: t('Preview'),
      style: { width: '80px', textAlign: 'center' },
      formatter: (cell, row) => (
        <Button
          size="sm"
          color="outline-primary"
          onClick={() => setPreviewTemplate(row)}
          title="Preview with Clinic Branding"
        >
          <RiEyeLine />
        </Button>
      ),
    },
  ];

  return (
    <React.Fragment>
      {/* Super Admin Managed Notice */}
      <Card className="border shadow-sm mb-3 bg-light border-start border-4 border-success">
        <CardBody className="py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="rounded-circle p-2 bg-success text-white font-size-18 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
              <RiShieldCheckLine />
            </div>
            <div>
              <h6 className="font-weight-bold text-dark mb-0">
                {t("Super Admin Curated Meta WhatsApp Cloud Templates")}
              </h6>
              <p className="text-muted font-size-12 mb-0">
                {t("WhatsApp message templates are centrally managed and pre-approved on the Meta WhatsApp Business Platform for high deliverability. All automated dispatches from your clinic dynamically populate your clinic branding and doctor details.")}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="border shadow-sm">
        <CardBody>
          <Datatables
            isSearch={true}
            columns={tableColumns}
            showTableOnly={true}
            rowsLength={rows?.length || 0}
            rows={rows || []}
            keyField={'id'}
            title={t("Approved WhatsApp Business Templates")}
            isAdd={false}
            isTableHead={true}
            loading={loading}
            ssr={() => { }}
          />
        </CardBody>
      </Card>

      {/* Live Preview Modal */}
      <Modal isOpen={Boolean(previewTemplate)} toggle={() => setPreviewTemplate(null)} size="md" centered>
        <ModalHeader toggle={() => setPreviewTemplate(null)}>
          <RiWhatsappLine className="text-success me-1 font-size-18" /> {previewTemplate?.title}
        </ModalHeader>
        <ModalBody className="p-4 bg-light">
          {previewTemplate && (
            <div
              className="p-3 rounded shadow-sm bg-white border"
              style={{
                maxWidth: '100%',
                fontFamily: 'Segoe UI, Helvetica, Arial, sans-serif',
              }}
            >
              {previewTemplate.header_text && (
                <div className="font-weight-bold text-dark font-size-14 mb-2 pb-1 border-bottom">
                  {previewTemplate.header_text}
                </div>
              )}
              <div className="text-dark font-size-13" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {getRenderedPreview(previewTemplate)}
              </div>
              {previewTemplate.footer_text && (
                <div className="text-muted font-size-11 mt-3 pt-1 border-top">
                  {previewTemplate.footer_text}
                </div>
              )}
              <div className="text-end text-muted font-size-10 mt-1">10:30 AM ✓✓</div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPreviewTemplate(null)}>
            {t("Close Preview")}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SMSTemplate;